<template>
    <div class="preview-page">
        <!-- PageHeader for desktop -->
        <PageHeader :active-section="activeSection" class="desktop-header" />

        <!-- TheHeader for mobile -->
        <TheHeader :relative="true" :hide-menu="true" class="mobile-header" />

        <div class="page-content">
            <!-- Home Slider Section -->
            <div id="home" class="preview-section home-slider-section">
                <div class="preview-content">
                    <HomeSlider :slides="settings?.homeSlider?.slides" :autoplay="true" :autoplay-duration="10000" />
                </div>
            </div>

            <div id="about" class="preview-section about-section">
                <div class="section-header">
                    <h2 class="section-title">ART IN ONE FIELD</h2>
                </div>
                <div class="section-content">
                    <p class="section-description">
                        Artfield showcases a curated selection of talents, where members and organizations explore portfolios, follow calendars and connect directly to bring creative projects to life.
                        <br /><br />
                        Through creative strategies from concept development to execution, Artfield guides brands in building meaningful projects and refined visual storytelling.
                    </p>
                </div>
            </div>

            <!-- Fields Section -->
            <div id="talents" class="preview-section fields-section">
                <div class="fields-layout">
                    <div class="fields-header">
                        <h2 class="section-title">FIELDS</h2>
                    </div>
                    <div class="field-categories">
                        <ul class="field-list">
                            <li v-for="(category, index) in settings?.fields?.categories" :key="category.id"
                                class="field-item" :class="{ 'active': selectedCategoryIndex === index }"
                                @mouseenter="selectCategory(index)">
                                {{ category.categoryTitle || category.title }}
                            </li>
                            <!-- Fallback categories from Figma if no settings are available -->
                            <li v-if="!settings?.fields?.categories || settings.fields.categories.length === 0"
                                v-for="(field, index) in ['Photography', 'Design', 'Direction', 'Hair & Makeup', 'Styling', 'Gallery', 'Studio']"
                                :key="field" class="field-item" :class="{ 'active': selectedCategoryIndex === index }"
                                @mouseenter="selectCategory(index)">
                                {{ field }}
                            </li>
                        </ul>
                    </div>
                    <div class="field-showcase">
                        <div class="field-image-container">
                            <img v-if="selectedCategory && selectedCategory.mediaUrl && selectedCategory.mediaType === 'image'"
                                :src="selectedCategory.mediaUrl"
                                :alt="selectedCategory.title || selectedCategory.categoryTitle"
                                class="field-showcase-image" />
                            <video
                                v-else-if="selectedCategory && selectedCategory.mediaUrl && selectedCategory.mediaType === 'video'"
                                :src="selectedCategory.mediaUrl" autoplay muted loop
                                class="field-showcase-video"></video>
                            <!-- Placeholder if no image available -->
                            <div v-else class="field-placeholder">Featured Field Work</div>

                            <div class="field-image-caption">
                                <h4>{{ selectedCategory ? (selectedCategory.title || selectedCategory.categoryTitle) :
                                    'Artists name' }}</h4>
                                <p>{{ selectedCategory ? (selectedCategory.tag || 'Impressum') : 'Impressum' }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Handpicked Works Section -->
            <div id="works" class="preview-section works-section">
                <div class="section-header">
                    <h2 class="section-title">GOT THE CREATIVE ITCH?</h2>
                    <p class="section-description">Pick your favorites and make your own board.</p>
                </div>
                <div class="preview-content">
                    <div class="works-grid">
                        <div v-for="(area, index) in settings?.handpickedWorks?.areas || []" :key="area?.id || index"
                            class="work-item" :class="{ 'small-item': index > 0 && index < 5 }">
                            <CustomWorkGridItem
                                @click="router.push(`/field/works/${area.works[areaWorkIndexes[index]]._id}`)"
                                v-if="area?.works && area.works.length > 0 && areaWorkIndexes[index] < area.works.length && area.works[areaWorkIndexes[index]]"
                                :work="area.works[areaWorkIndexes[index]]" />
                            <div v-else class="work-placeholder">
                                <div class="artist-caption">
                                    <h4>Artists name</h4>
                                    <p>Impressum</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Collect Reads Section -->
            <div id="reads" class="preview-section reads-section">
                <div class="section-header">
                    <h2 class="section-title">COLLECT READS</h2>
                    <p class="section-description">Save stories, Q&A's, and interviews from the art world.</p>
                </div>
                <div class="preview-content">
                    <div class="reads-scroll-container">
                        <div v-for="(read, index) in settings?.collectReads?.reads || placeholderReads"
                            :key="read?._id || `placeholder-${index}`" class="read-item-wrapper">
                            <ReadGridItemAlt v-if="read" :read="read" />
                            <div v-else class="read-placeholder" :style="getReadColor(index)">
                                <div class="read-caption">
                                    <h4>Article name</h4>
                                    <p>Article category</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Download Now Section -->
            <div id="download" class="preview-section download-section">
                <div class="section-header">
                    <h2 class="section-title">DOWNLOAD NOW!</h2>
                    <p class="section-description">Download the Artfield app & get into creative flow.</p>
                </div>
                <div class="download-content">
                    <div class="download-media">
                        <img v-if="settings?.downloadNow?.media && settings.downloadNow.media.mediaType === 'image'"
                            :src="settings.downloadNow.media.mediaUrl" :alt="settings.downloadNow.media.title"
                            class="download-image" />
                        <video
                            v-else-if="settings?.downloadNow?.media && settings.downloadNow.media.mediaType === 'video'"
                            :src="settings.downloadNow.media.mediaUrl" controls class="download-video"></video>
                        <div v-else class="download-placeholder">App Screenshot</div>
                    </div>
                    <div class="store-logos">
                        <a v-if="settings?.downloadNow?.appStoreUrl" :href="settings.downloadNow.appStoreUrl"
                            target="_blank" class="store-logo-link">
                            <img src="/logo/appstore-artfield.png" alt="App Store" class="store-logo" />
                        </a>
                        <a v-if="settings?.downloadNow?.playStoreUrl" :href="settings.downloadNow.playStoreUrl"
                            target="_blank" class="store-logo-link">
                            <img src="/logo/playstore-artfield.png" alt="Play Store" class="store-logo" />
                        </a>
                    </div>
                </div>
            </div>

            <!-- Contact Section -->
            <div id="contact" class="preview-section contact-section">
                <div class="section-header">
                    <h2 class="section-title">CONTACT</h2>
                    <p class="section-description">
                        <a href="mailto:contact@artfieldofficial.com"
                            class="contact-email">contact@artfieldofficial.com</a>
                    </p>
                </div>
            </div>
        </div>

        <TheFooter />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useSettingsStore } from '~/composables/useSettingsStore'
import CustomWorkGridItem from '~/components/works/CustomWorkGridItem.vue'
import ReadGridItemAlt from '~/components/reads/ReadGridItemAlt.vue'
import PageHeader from '~/components/layout/PageHeader.vue'
import TheHeader from '~/components/core/TheHeader.vue'
import TheFooter from '~/components/core/TheFooter.vue'
import HomeSlider from '~/components/slider/HomeSlider.vue'
import { useRouter } from 'vue-router'
const {fetchUser} = useUserStore();

const router = useRouter()
definePageMeta({
    layout: 'default'
})

const { getSettings } = useSettingsStore()
const settings = ref<any>(null)
const activeSection = ref('home')
const selectedCategoryIndex = ref(0)

// Placeholder read colors from Figma design
const readColors = ['#75FA90', '#FFF0DA', '#FBFF3A', '#4F00B4', '#FF3300']

// Placeholder reads data if none available in settings
const placeholderReads = ref(Array(5).fill(null))

// Store the current index for each handpicked work area
const areaWorkIndexes = ref<number[]>([0, 0, 0, 0, 0, 0])

// Store all rotation intervals
const rotationIntervals = ref<NodeJS.Timeout[]>([])

// Computed property to get the currently selected category
const selectedCategory = computed(() => {
    if (!settings.value?.fields?.categories ||
        settings.value.fields.categories.length === 0 ||
        selectedCategoryIndex.value >= settings.value.fields.categories.length) {
        return null;
    }
    return settings.value.fields.categories[selectedCategoryIndex.value];
});

useHead({
  title: "Artfield - Discover Talents & Creative Solutions",
  meta: [
    {
      name: "description",
      content:
          "Explore portfolios, follow calendars and meet creatives.",
    },
    {
      property: "og:title",
      content: "Artfield - Discover Talents & Creative Solutions",
    },
    {
      property: "og:description",
      content:
          "Explore portfolios, follow calendars and meet creatives.",
    },
    {
      property: "og:image",
      content: "https://artfield.app/favicon.png"
    },
    {
      name: "keywords",
      content: "Artfield, Talents, Creative Solutions, Portfolios, Art Community",
    },
  ],
});
// Function to select a category
const selectCategory = (index: number) => {
    selectedCategoryIndex.value = index;
};

// Function to get color for read items
const getReadColor = (index: number) => {
    return { backgroundColor: readColors[index % readColors.length] }
}

// Rotate works for a specific area
const rotateWorkForArea = (areaIndex: number) => {
  if (!settings.value?.handpickedWorks?.areas) return
  
  const area = settings.value.handpickedWorks.areas[areaIndex]
  if (area?.works && area.works.length > 0) {
    areaWorkIndexes.value[areaIndex] = (areaWorkIndexes.value[areaIndex] + 1) % area.works.length
  }
}

// Setup staggered rotations on component mount
onMounted(async () => {
  try {
    await fetchUser();

    settings.value = await getSettings()
    console.log('settings', settings.value)

    // Initialize selected category to first one if available
    if (settings.value?.fields?.categories &&
        settings.value.fields.categories.length > 0) {
        selectedCategoryIndex.value = 0
    }

    // Initialize work indexes for each area
    if (settings.value?.handpickedWorks?.areas) {
      settings.value.handpickedWorks.areas.forEach((area: any, index: number) => {
        if (index < areaWorkIndexes.value.length) {
          areaWorkIndexes.value[index] = 0
          
          // Create staggered intervals for each area
          // Base interval is 15000ms, add different offsets for each area
          const baseInterval = 20000
          const staggerOffset = index * 9000 // 3 second offset between areas
          
          // Set a timeout to start the interval with the staggered delay
          setTimeout(() => {
            const interval = setInterval(() => rotateWorkForArea(index), baseInterval)
            rotationIntervals.value.push(interval)
          }, staggerOffset)
        }
      })
    }
  } catch (error) {
    console.error('Failed to load settings:', error)
  }

  // Set up scroll listener to track active section
  window.addEventListener('scroll', handleScroll)
})

// Track which section is currently visible
const handleScroll = () => {
    const sections = ['home', 'about', 'talents', 'reads', 'download', 'contact']

    for (const section of sections) {
        const element = document.getElementById(section)
        if (element) {
            const rect = element.getBoundingClientRect()
            // Check if section is in viewport (with some buffer)
            if (rect.top <= 150 && rect.bottom >= 150) {
                activeSection.value = section
                break
            }
        }
    }
}

onBeforeUnmount(() => {
  // Clear all rotation intervals
  rotationIntervals.value.forEach(interval => clearInterval(interval))
  rotationIntervals.value = []
  
  // Remove scroll listener
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
/* Overall page styles */
.preview-page {
    background-color: #FFFFFF;
    color: #000000;
    font-family: 'ABC Walter Neue', sans-serif;
}

/* Header visibility based on screen size */
.desktop-header {
    display: block;
}

.mobile-header {
    display: none;
}

.page-content {
    max-width: 1440px;
    margin: 0 auto;
    padding-top: calc(var(--header-height) + 20px);
    /* Header height plus spacing */
    border-bottom: 1px solid #000;
}

/* Common section styles */
.preview-section {
    padding: 40px 40px 20px;
    display: flex;
    flex-direction: column;
}

.about-section {
    display: flex;
    flex-direction: row;
    padding: 80px 40px;
}

.about-section .section-content {
    flex: 1;
}

.about-section .section-header {
    flex: 1;
}

.section-header {
    margin-bottom: 40px;
}

.section-title {
    font-size: 40px;
    font-weight: 400;
    line-height: 1;
    margin-bottom: 20px;
}

.section-description {
    font-size: 20px;
    line-height: 1.5em;
    font-weight: 400;
    max-width: 660px;
}

.home-slider-section {
    padding-top: 80px;
}

.preview-content {
    width: 100%;
}

/* Fields Section */
.fields-section {
    padding: 40px 40px 120px;
}

.fields-layout {
    display: flex;
    flex-wrap: wrap;
    gap: 40px;
}

.fields-header, 
.field-categories, 
.field-showcase {
    flex: 1;
}

.fields-header .section-title {
    margin-top: 0;
}

.field-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    list-style: none;
    padding: 0;
    margin: 0;
}

.field-item {
    font-size: 40px;
    font-weight: 400;
    line-height: 1;
    text-transform: uppercase;
    cursor: pointer;
    opacity: 0.5;
    transition: opacity 0.3s ease;
}

.field-item:hover {
    opacity: 0.8;
}

.field-item.active {
    opacity: 1;
}

.field-showcase {
    width: 100%;
}

.field-image-container {
    width: 100%;
    aspect-ratio: 1/1;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
}

.field-showcase-image,
.field-showcase-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.field-placeholder {
    width: 100%;
    height: 100%;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #666;
}

.field-image-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px;
    cursor: pointer;
}

.field-image-caption h4 {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.65em;
    color: #FFFFFF;
    margin: 0 0 10px 0;
}

.field-image-caption p {
    font-size: 10px;
    font-weight: 400;
    line-height: 1.3em;
    color: #FFFFFF;
    margin: 0;
}

/* Works Section */
.works-section {
    padding: 40px 40px 120px;
}

.works-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
}

.work-item {
    position: relative;
    height: 300px;
    overflow: hidden;
    border-radius: 10px;
    background-color: #f0f0f0;
    grid-column: span 1;
}

/* Desktop layout pattern: 2-1-1-1-1-2 */
@media (min-width: 769px) {
    .work-item:nth-child(1),
    .work-item:nth-child(6) {
        grid-column: span 2;
    }
}

.work-placeholder {
    width: 100%;
    height: 100%;
    position: relative;
}

.artist-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 20px;
    background: rgba(0, 0, 0, 0.2);
}

.artist-caption h4 {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.65em;
    color: #FFFFFF;
    margin: 0 0 10px 0;
}

.artist-caption p {
    font-size: 10px;
    font-weight: 400;
    line-height: 1.3em;
    color: #FFFFFF;
    margin: 0;
}

/* Reads Section */
.reads-section {
    padding: 40px 40px 120px;
}
.reads-scroll-container {
    display: flex;
    gap: 20px;
    overflow-x: auto;
    padding-bottom: 20px;
    scrollbar-width: thin;
    scrollbar-color: #888 #f1f1f1;
    width: 100%;
}

.read-item-wrapper {
    flex: 0 0 23%;
    position: relative;
}

.read-placeholder {
    border-radius: 10px;
    aspect-ratio: 1/1;
    padding: 20px;
    position: relative;
}

.read-caption {
    position: absolute;
    bottom: -40px;
    left: 0;
    width: 100%;
}

.read-caption h4 {
    font-size: 16px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0 0 5px 0;
}

.read-caption p {
    font-size: 12px;
    margin: 0;
}

/* Download Section */
.download-section {
    padding: 40px 40px 120px;
}

.download-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    margin: 0 auto;
}

.download-media {
    width: 100%;
    border-radius: 10px;
    overflow: hidden;
    background-color: #000;
}

.download-image,
.download-video {
    width: 100%;
    height: auto;
    display: block;
}

.download-placeholder {
    width: 100%;
    height: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #fff;
}

.store-logos {
    display: flex;
    gap: 20px;
    justify-content: center;
}

.store-logo-link {
    text-decoration: none;
    transition: opacity 0.2s;
}

.store-logo-link img {
    width: 255px;
    height: auto;
    object-fit: contain;
}

.store-logo-link:hover {
    opacity: 0.8;
}

.store-logo {
    height: 40px;
}

/* Contact Section */
.contact-section {
    padding: 40px 40px 160px;
}

.contact-email {
    color: #000;
    text-decoration: none;
    transition: opacity 0.2s;
}

.contact-email:hover {
    opacity: 0.7;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
    .works-grid {
        grid-template-columns: repeat(3, 1fr);
    }

    .field-categories, 
    .field-showcase {
        width: 100%;
    }
}

@media (max-width: 768px) {
    /* Header styles */
    .desktop-header {
        display: none;
    }
    
    .mobile-header {
        display: block;
        border-bottom: 1px solid #000;
    }
    
    /* Page layout */
    .page-content {
        padding: 16px;
    }
    
    .preview-section {
        padding: 40px 20px 80px;
    }
    
    .section-title {
        font-size: 24px;
        margin-bottom: 10px;
    }
    
    .section-description {
        font-size: 16px;
        line-height: 1.4;
    }
    
    /* About section */
    .about-section {
        flex-direction: column;
        padding: 40px 20px;
    }
    
    .about-section .section-header,
    .about-section .section-content {
        flex: none;
        width: 100%;
    }
    
    /* Home slider section */
    .home-slider-section {
        padding: 0;
        margin-bottom: 20px;
    }
    
    /* Fields section */
    .fields-section {
        padding: 30px 20px;
    }
    
    .fields-layout {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 20px;
    }
    
    .fields-header, 
    .field-categories {
        flex: 1;
        width: 50%;
    }
    
    .field-showcase {
        flex: 0 0 100%;
        width: 100%;
        margin-top: 20px;
    }
    
    .field-list {
        gap: 10px;
    }
    
    .field-item {
        font-size: 20px;
    }
    
    /* Works section - Mobile layout pattern */
    .works-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 10px;
    }
    
    /* First reset all items to normal sizing */
    .work-item {
        grid-column: span 1;
        height: 200px;
    }
    
    /* Then apply the specific pattern for mobile: only first item spans 2 */
    .work-item:nth-child(1) {
        grid-column: span 2;
    }
    
    /* Every 3rd item starting from 3rd item spans 2 in mobile view */
    .work-item:nth-of-type(3n+1):not(:first-child):not(:nth-child(6)) {
        grid-column: span 2;
    }
    
    /* Reads section */
    .read-item-wrapper {
        flex: 0 0 250px;
        min-height: 320px;
    }
    
    /* Download section */
    .download-content {
        gap: 20px;
    }
}

@media (max-width: 480px) {
    .preview-section {
        padding: 30px 15px 60px;
    }
    
    .fields-section {
        padding: 20px 15px;
    }
    
    .fields-layout {
        gap: 15px;
    }
    
    .field-list {
        gap: 8px;
    }
    
    .field-item {
        font-size: 16px;
    }
    
    /* Works section - ensure same pattern on smaller screens */
    .works-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }
    
    /* First reset all items */
    .work-item {
        grid-column: span 1;
        height: 180px;
    }
    
    /* Apply the 1-2-1-2 pattern */
    .work-item:nth-of-type(3n + 1) {
        grid-column: span 2;
    }
    .reads-section {
        padding: 0 0 64px 0;
        margin-right: -40px;
    }
    .reads-section .section-header {
        padding-bottom: 32px;
    }
    .download-section {
        padding: 0 0 64px 0;
    }
    .download-section .section-header {
        padding-bottom: 32px;
    }
    .download-image, .download-video {
        aspect-ratio: 1/1;
        object-fit: cover;
    }
    .contact-section {
        padding: 0 0 64px 0;
    }
    .works-section {
        padding: 0 0 64px 0;
    }
    
    .works-section .section-header {
        padding-bottom: 32px;
    }
    .reads-scroll-container {
        gap: 8px;
    }
    .read-item-wrapper {
        flex: 0 0 175px;
        min-height: 223px;
    }
    .about-section {
        padding: 16px 0;
    }
    
    .home-slider-section {
        padding: 0;
        min-height: 500px;
        margin-bottom: 16px;
    }
    
    .section-header {
        margin-bottom: 0;
    }
    
    .about-section .section-content {
        padding-bottom: 64px;
    }
    
    .fields-section {
        padding: 0 0 64px 0;
    }
}
</style>